import { ImmomioSpinner } from '@immomio/ui/spinner';
import { ImmomioButtonComponent } from '@immomio/ui/button';
import {
  Component,
  computed,
  inject,
  input,
  OnInit,
  DestroyRef,
  ChangeDetectionStrategy,
  effect,
  signal,
  untracked
} from '@angular/core';

import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { filter, map, take } from 'rxjs/operators';

import { Observable, Subject } from 'rxjs';
import { Store } from '@ngrx/store';

import {
  Constants,
  PropertyMatchBean,
  SelfDisclosureModel,
  SelfDisclosureQuestion,
  SelfDisclosureQuestionType
} from '@ui/shared/model';

import {
  getConstants,
  ModalContentComponent,
  ModalFooterComponent,
  ModalV2Component,
  SelfDisclosureFormComponent
} from '@ui/legacy-lib';
import { UserStore } from '@ui/tenant-pool/shared/user/data-access';
import { WbsCertificateStore } from '@ui/tenant-pool/shared/housing-permission/certificate/data-access';

import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { DeclareIntent } from '../../../+state/property-matches/property-matches.actions';
import { getSaveSelfDisclosureAnswersActionState } from '../../../+state/self-disclosure/self-disclosure.selectors';
import { SaveSelfDisclosureAnswers } from '../../../+state/self-disclosure/self-disclosure.actions';
import { SelfDisclosureState } from '../../../+state/self-disclosure/self-disclosure.reducers';

@Component({
  selector: 'app-self-disclosure-modal',
  templateUrl: './self-disclosure-modal.component.html',
  styleUrls: ['./self-disclosure-modal.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ImmomioSpinner,
    ImmomioButtonComponent,
    SelfDisclosureFormComponent,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    AsyncPipe,
    ModalV2Component,
    ModalContentComponent,
    ModalFooterComponent
  ]
})
export class SelfDisclosureModalComponent implements OnInit {
  #destroyRef = inject(DestroyRef);
  public isProcessing$: Observable<boolean>;
  public answeredSelfDisclosure: SelfDisclosureModel;
  public selfDisclosureModel = input<SelfDisclosureModel>(undefined);
  public selfDisclosureResponses = input<SelfDisclosureModel>(undefined);
  public propertyMatch = input<PropertyMatchBean>();
  public constants$: Observable<Constants>;
  public control = new FormControl();
  public formValiditySubject = new Subject<boolean>();
  public mandatoryFieldsMissing: boolean;
  public hasIntent = input<boolean>();
  private ngbActiveModal = inject(NgbActiveModal);
  private store = inject<Store<SelfDisclosureState>>(Store);
  private userStore = inject(UserStore);

  protected readonly userData = this.userStore.userData;

  readonly #wbsCertificateStore = inject(WbsCertificateStore);
  readonly #isWbsCertificateRequested = signal(false);
  protected readonly wbsDocuments = computed(
    () => this.#wbsCertificateStore.wbsCertificate()?.documents ?? []
  );
  /**
   * The form pre-fills only once, so it has to wait for the WBS. Guests have
   * no profile to pre-fill from and therefore no WBS to wait for.
   */
  protected readonly wbsCertificateLoaded = computed(() => {
    const { done, error } =
      this.#wbsCertificateStore.loadWbsCertificateActionState();
    return (
      !this.userData() ||
      (this.#isWbsCertificateRequested() && (done || !!error))
    );
  });

  constructor() {
    // The user may only arrive after the modal has opened.
    effect(() => {
      const hasUser = !!this.userData();
      untracked(() => {
        if (hasUser && !this.#isWbsCertificateRequested()) {
          this.#isWbsCertificateRequested.set(true);
          this.#wbsCertificateStore.loadWbsCertificate();
        }
      });
    });
  }

  get mandatoryFieldsHasAnswer() {
    return this.answeredSelfDisclosure.questions.every(question => {
      const foundQuestion = (
        this.control.value as SelfDisclosureModel
      ).questions.find(q => q.id === question.id);
      const answer = foundQuestion?.answer ?? null;
      return (
        !question.mandatory ||
        foundQuestion.answerUnavailable ||
        (!!answer && answer.length > 0) ||
        (!!answer && Object.keys(answer).length > 0) ||
        (question.type === SelfDisclosureQuestionType.BOOLEAN &&
          (answer || answer === false))
      );
    });
  }

  public ngOnInit() {
    this.isProcessing$ = this.store
      .select(getSaveSelfDisclosureAnswersActionState)
      .pipe(map(state => state.pending));

    this.constants$ = this.store.select(getConstants);

    this.answeredSelfDisclosure = {
      ...this.selfDisclosureModel(),
      // filter questions that are hidden (hidden flag is mapped in graphQL to showSelfDisclosureQuestions)
      questions: this.selfDisclosureModel().questions
        ? this.selfDisclosureModel().questions.filter(
            q => q.showSelfDisclosureQuestions
          )
        : [],
      confirmations: this.selfDisclosureModel().confirmations || [],
      documents: this.selfDisclosureModel().documents || [],
      description: this.selfDisclosureModel().description || '',
      feedbackEmail: this.selfDisclosureModel().feedbackEmail || '',
      propertySearcherComment:
        this.selfDisclosureModel().propertySearcherComment || ''
    };

    if (
      this.selfDisclosureResponses() &&
      this.selfDisclosureResponses().questions
    ) {
      this.answeredSelfDisclosure.isAnswered = true;
      this.answeredSelfDisclosure.propertySearcherComment =
        this.selfDisclosureResponses().propertySearcherComment;
      this.answeredSelfDisclosure.uploadedDocuments =
        this.selfDisclosureResponses().uploadedDocuments;
      this.answeredSelfDisclosure.questions =
        this.answeredSelfDisclosure.questions.map(question => {
          return this.getAnsweredQuestion(
            question,
            this.selfDisclosureResponses().questions
          );
        });
    }
    this.control.patchValue(this.answeredSelfDisclosure);
    this.formValiditySubject
      .asObservable()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((isValid: boolean) => {
        this.mandatoryFieldsMissing = isValid;
      });
  }

  public onFormValidityChange(isValid: boolean) {
    this.control.setErrors(isValid ? null : { missingFields: true });
  }

  public dismiss() {
    this.ngbActiveModal.dismiss();
  }

  public save() {
    if (!this.mandatoryFieldsHasAnswer || !this.control.valid) {
      this.formValiditySubject.next(true);
      return;
    }
    this.formValiditySubject.next(false);
    this.formValiditySubject.complete();

    const value = {
      ...this.control.value,
      confirmations: this.selfDisclosureModel().confirmations,
      documents: this.selfDisclosureModel().documents
    };

    this.store.dispatch(
      new SaveSelfDisclosureAnswers(this.propertyMatch().property.id, value)
    );

    this.control.setErrors(null);

    this.store
      .select(getSaveSelfDisclosureAnswersActionState)
      .pipe(
        map(state => state.done),
        filter(done => !!done),
        take(1)
      )
      .subscribe(() => {
        if (this.hasIntent()) {
          this.store.dispatch(
            DeclareIntent({
              declareIntentData: {
                intent: true,
                propertySearcherId: this.propertyMatch().id
              }
            })
          );
        }
        this.ngbActiveModal.close();
      });
  }

  private getAnsweredQuestion(
    question: SelfDisclosureQuestion,
    responses: SelfDisclosureQuestion[]
  ) {
    const answer = this.findQuestionByTitle(responses, question.title);
    return answer ? this.answerQuestion(question, answer) : question;
  }

  private answerQuestion(
    question: SelfDisclosureQuestion,
    answer: SelfDisclosureQuestion
  ) {
    const answeredQuestion = this.addAnswerToQuestion(question, answer);
    if (answeredQuestion.subQuestions && answer.subQuestions) {
      answeredQuestion.subQuestions = answeredQuestion.subQuestions.map(
        subQuestion => {
          const answerSubQuestion = this.findQuestionByTitle(
            answer.subQuestions,
            subQuestion.title
          );
          if (answerSubQuestion) {
            return this.addAnswerToQuestion(subQuestion, answerSubQuestion);
          }
          return subQuestion;
        }
      );
    }
    return answeredQuestion;
  }

  private addAnswerToQuestion(
    question: SelfDisclosureQuestion,
    answer: SelfDisclosureQuestion
  ) {
    return {
      ...question,
      // we need to account for value 'false' of questions type BOOLEAN
      answer:
        answer.answer || answer.answer === false
          ? answer.answer
          : this.getDefaultAnswer(question),
      comment: answer.comment,
      uploads: answer.uploads,
      answerUnavailable: answer.answerUnavailable,
      confirmations: answer.confirmations
    };
  }

  private getDefaultAnswer(question: SelfDisclosureQuestion) {
    if (
      question.type === SelfDisclosureQuestionType.PERSONS ||
      question.type === SelfDisclosureQuestionType.CHILDREN
    ) {
      return [];
    } else if (
      question.type === SelfDisclosureQuestionType.BOOLEAN ||
      question.type === SelfDisclosureQuestionType.IBAN ||
      question.type === SelfDisclosureQuestionType.TAX_ID
    ) {
      /*
       * This MUST be null, or the PDF download breaks. BE parses for boolean and e.g. an empty
       * object will result in a parsing error:
       * https://app.asana.com/0/771015745353211/1146597974985705
       *
       * Similarly for IBAN and Tax ID, if this isn't null, you get "[object Object]" as the default input value
       */
      return null;
    }
    return {};
  }

  private findQuestionByTitle(array: SelfDisclosureQuestion[], title: string) {
    return array.find(question => question.title === title);
  }
}
